import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import {
  useLeads,
  useLeadStages,
  useEnhancedLeadStats,
  useLeadsNeedingAttention,
  useArchivedLeads,
  useUpcomingFollowUps,
  useUpdateLead,
  useUpdateFollowUp,
} from '@/hooks/useLeads'
import type { LeadFilters, LeadSort, LeadStatus, LeadSource } from '@limo/shared'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert'
import {
  Search,
  Users,
  TrendingUp,
  Clock,
  Target,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  ArrowUpDown,
  Plus,
  LayoutList,
  Kanban,
  Flame,
  PhoneCall,
  AlertTriangle,
  Archive,
  Trophy,
  ThumbsDown,
  RotateCcw,
  Download,
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import {
  LeadScoreBadge,
  LeadStatusBadge,
  LeadStageBadge,
  LeadKanbanBoard,
  LeadAgeIndicator,
  LeadStageButtons,
  LeadQuickActionsRow,
  LeadOutcomeDialog,
  useLeadOutcomeDialog,
  LeadKeyboardHelp,
  LeadKeyboardHint,
  QuickFollowUpDialog,
} from '@/components/leads'
import { useToast } from '@/hooks/use-toast'
import { downloadCsv, rowsToCsv } from '@/lib/reporting/csv'
import { useLeadKeyboardNavigation } from '@/hooks/useLeadKeyboardNavigation'
import type { LeadStage } from '@limo/shared'
import { cn } from '@limo/shared'

export function LeadsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { isImpersonating, impersonatedAccount } = useImpersonation()

  // Build base path for navigation (supports impersonation)
  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  // Main tab state (active vs archive vs follow-ups)
  const [mainTab, setMainTab] = useState<'active' | 'archive' | 'follow-ups'>('active')

  // View mode state (persisted in localStorage)
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>(() => {
    const stored = localStorage.getItem('leads-view-mode')
    return (stored === 'kanban' || stored === 'list') ? stored : 'list'
  })

  // Persist view mode
  useEffect(() => {
    localStorage.setItem('leads-view-mode', viewMode)
  }, [viewMode])

  // Active leads state
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<LeadStatus[]>(['open'])
  const [sourceFilter, setSourceFilter] = useState<LeadSource[]>([])
  const [stageFilter, setStageFilter] = useState<string[]>([])
  const [stageFilterInitialized, setStageFilterInitialized] = useState(false)
  const [page, setPage] = useState(1)
  const [sortField, setSortField] = useState<LeadSort['field']>('created_at')
  const [sortDirection, setSortDirection] = useState<LeadSort['direction']>('desc')
  const pageSize = 25

  // Archive state
  const [archiveOutcome, setArchiveOutcome] = useState<'all' | 'won' | 'lost'>('all')
  const [archivePage, setArchivePage] = useState(1)

  // Follow-ups tab state
  const [followUpFilter, setFollowUpFilter] = useState<'all' | 'overdue' | 'today' | 'upcoming'>('all')
  const [followUpPage, setFollowUpPage] = useState(1)

  // Dismiss needs attention alert
  const [attentionDismissed, setAttentionDismissed] = useState(false)

  // Build filters object - default to open leads only
  const filters: LeadFilters = useMemo(() => ({
    search: searchQuery || undefined,
    status: statusFilter.length > 0 ? statusFilter : ['open'],
    sources: sourceFilter.length > 0 ? sourceFilter : undefined,
    stageIds: stageFilter.length > 0 ? stageFilter : undefined,
  }), [searchQuery, statusFilter, sourceFilter, stageFilter])

  const sort: LeadSort = useMemo(() => ({
    field: sortField,
    direction: sortDirection,
  }), [sortField, sortDirection])

  // Fetch data
  const { data: leadsData, isLoading: leadsLoading } = useLeads(filters, sort, page, pageSize)
  const { data: stats, isLoading: statsLoading } = useEnhancedLeadStats()
  const { data: stages } = useLeadStages()

  // Initialize stage filter to "New Lead" when stages load for the first time
  useEffect(() => {
    if (stages && stages.length > 0 && !stageFilterInitialized) {
      const newLeadStage = stages.find(s => s.name === 'New Lead' && !s.isWon && !s.isLost)
      if (newLeadStage) {
        setStageFilter([newLeadStage.id])
      }
      setStageFilterInitialized(true)
    }
  }, [stages, stageFilterInitialized])

  const { data: attentionData } = useLeadsNeedingAttention()
  const { data: archiveData, isLoading: archiveLoading } = useArchivedLeads(
    archiveOutcome,
    { field: 'updated_at', direction: 'desc' },
    archivePage,
    pageSize
  )

  // Follow-ups data
  const { data: followUpsData, isLoading: followUpsLoading } = useUpcomingFollowUps(
    followUpFilter,
    followUpPage,
    pageSize
  )

  // Mutations
  const updateLead = useUpdateLead()
  const updateFollowUp = useUpdateFollowUp()

  // Quick follow-up dialog state
  const [quickFollowUpLead, setQuickFollowUpLead] = useState<{
    id: string
    name: string
  } | null>(null)

  // Outcome dialog state
  const outcomeDialog = useLeadOutcomeDialog()
  const [selectedLeadForOutcome, setSelectedLeadForOutcome] = useState<{
    id: string
    name: string
    value?: number
  } | null>(null)

  // Handle stage change (optimistic update)
  const handleStageChange = async (leadId: string, stageId: string) => {
    try {
      await updateLead.mutateAsync({ leadId, stageId })
      toast({
        title: 'Stage updated',
        description: 'Lead moved to new stage.',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update stage',
        variant: 'destructive',
      })
    }
  }

  // Handle marking won
  const handleMarkWon = (leadId: string, name: string, value?: number) => {
    setSelectedLeadForOutcome({ id: leadId, name, value })
    outcomeDialog.openWon()
  }

  // Handle marking lost
  const handleMarkLost = (leadId: string, name: string, value?: number) => {
    setSelectedLeadForOutcome({ id: leadId, name, value })
    outcomeDialog.openLost()
  }

  // Submit outcome
  const handleOutcomeSubmit = async (notes?: string, lostReason?: string) => {
    if (!selectedLeadForOutcome) return

    const wonStage = stages?.find(s => s.isWon)
    const lostStage = stages?.find(s => s.isLost)

    try {
      await updateLead.mutateAsync({
        leadId: selectedLeadForOutcome.id,
        status: outcomeDialog.type === 'won' ? 'won' : 'lost',
        stageId: outcomeDialog.type === 'won' ? wonStage?.id : lostStage?.id,
        lostReason: outcomeDialog.type === 'lost' ? lostReason : undefined,
        outcomeNotes: notes,
      })
      toast({
        title: outcomeDialog.type === 'won' ? 'Lead marked as won!' : 'Lead marked as lost',
        description: outcomeDialog.type === 'won'
          ? 'Congratulations on closing this deal!'
          : 'Lead has been moved to the archive.',
      })
      outcomeDialog.close()
      setSelectedLeadForOutcome(null)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update lead',
        variant: 'destructive',
      })
    }
  }

  // Keyboard navigation (enabled only in list view on active tab)
  const keyboardNav = useLeadKeyboardNavigation({
    leads: leadsData?.leads || [],
    stages: stages || [],
    enabled: mainTab === 'active' && viewMode === 'list',
    onOpenDetail: (leadId) => navigate(`${basePath}/leads/${leadId}`),
    onCall: (leadId) => {
      const lead = leadsData?.leads.find(l => l.id === leadId)
      if (lead?.customerPhone) {
        window.open(`tel:${lead.customerPhone}`, '_self')
      }
    },
    onEmail: (leadId) => {
      const lead = leadsData?.leads.find(l => l.id === leadId)
      if (lead?.customerEmail) {
        window.open(`mailto:${lead.customerEmail}`, '_self')
      }
    },
    onStageChange: handleStageChange,
    onMarkWon: (leadId) => {
      const lead = leadsData?.leads.find(l => l.id === leadId)
      if (lead) {
        handleMarkWon(leadId, lead.customerName || 'Lead', lead.estimatedValue || undefined)
      }
    },
    onMarkLost: (leadId) => {
      const lead = leadsData?.leads.find(l => l.id === leadId)
      if (lead) {
        handleMarkLost(leadId, lead.customerName || 'Lead', lead.estimatedValue || undefined)
      }
    },
  })

  // Active filter count (excluding the default 'open' status and default 'New Lead' stage)
  const newLeadStageId = stages?.find(s => s.name === 'New Lead' && !s.isWon && !s.isLost)?.id
  const isDefaultStageFilter = stageFilter.length === 1 && stageFilter[0] === newLeadStageId
  const activeFilterCount = [
    statusFilter.length > 0 && !(statusFilter.length === 1 && statusFilter[0] === 'open'),
    sourceFilter.length > 0,
    stageFilter.length > 0 && !isDefaultStageFilter,
  ].filter(Boolean).length

  // Format helpers
  const formatCurrency = (amount: number | null | undefined) =>
    amount != null
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amount)
      : '-'

  const formatDateTime = (dateStr: string | null | undefined) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      : '-'

  const formatResponseTime = (minutes: number | null | undefined) => {
    if (minutes == null) return '-'
    if (minutes < 60) return `${minutes}m`
    if (minutes < 1440) return `${Math.round(minutes / 60)}h`
    return `${Math.round(minutes / 1440)}d`
  }

  // Sort handler
  const handleSort = (field: LeadSort['field']) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
    setPage(1)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('')
    setStatusFilter(['open'])
    setSourceFilter([])
    const defaultStage = stages?.find(s => s.name === 'New Lead' && !s.isWon && !s.isLost)
    setStageFilter(defaultStage ? [defaultStage.id] : [])
    setPage(1)
  }

  // Handle reopen lead from archive
  const handleReopenLead = async (leadId: string) => {
    try {
      // Get the default stage
      const defaultStage = stages?.find(s => s.isDefault)
      await updateLead.mutateAsync({
        leadId,
        status: 'open',
        stageId: defaultStage?.id,
        lostReason: undefined,
        outcomeNotes: undefined,
      })
      toast({
        title: 'Lead reopened',
        description: 'The lead has been moved back to the active pipeline.',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to reopen lead',
        variant: 'destructive',
      })
    }
  }

  // Handle export CSV
  const handleExportCSV = () => {
    const leads = mainTab === 'active' ? leadsData?.leads : archiveData?.leads
    if (!leads || leads.length === 0) return

    const headers = ['Name', 'Email', 'Phone', 'Stage', 'Source', 'Status', 'Score', 'Value', 'Created']
    const rows = leads.map(lead => [
      lead.customerName || '',
      lead.customerEmail || '',
      lead.customerPhone || '',
      lead.stage?.name || '',
      lead.source || '',
      lead.status || '',
      lead.score || 0,
      lead.estimatedValue || 0,
      new Date(lead.createdAt).toLocaleDateString(),
    ])

    // Shared writer: correct quoting for embedded commas/quotes/newlines plus a
    // spreadsheet formula-injection guard, neither of which the previous
    // hand-rolled join handled.
    downloadCsv(
      `leads-${mainTab}-${new Date().toISOString().split('T')[0]}.csv`,
      rowsToCsv(headers, rows)
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Leads</h1>
          <div className="flex items-center gap-4">
            <p className="text-muted-foreground">
              Manage and track your sales leads through the pipeline
            </p>
            <LeadKeyboardHint />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button onClick={() => navigate(`${basePath}/leads/new`)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Needs Attention Alert */}
      {!attentionDismissed && attentionData && attentionData.totalCount > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Needs Attention</AlertTitle>
          <AlertDescription className="text-amber-700">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                {attentionData.awaitingContact.length > 0 && (
                  <span>
                    {attentionData.awaitingContact.length} lead{attentionData.awaitingContact.length !== 1 ? 's' : ''} waiting over 30 min for first contact
                  </span>
                )}
                {attentionData.overdueFollowUps.length > 0 && (
                  <span>
                    {attentionData.overdueFollowUps.length} overdue follow-up{attentionData.overdueFollowUps.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAttentionDismissed(true)}
                className="text-amber-700 hover:text-amber-800 hover:bg-amber-100"
              >
                Dismiss
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards - Enhanced for sales focus */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Today</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats?.newToday || 0}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Awaiting Contact</CardTitle>
            <PhoneCall className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-amber-600">
                {stats?.awaitingContact || 0}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">
                {formatResponseTime(stats?.avgResponseMinutes)}
              </div>
            )}
            <p className="text-xs text-muted-foreground">to first contact</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Hot Leads</CardTitle>
            <Flame className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-red-600">
                {stats?.hotLeads || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground">score 70+</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-green-600">
                {stats?.conversionRate || 0}%
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs (Active / Archive) */}
      <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as 'active' | 'archive' | 'follow-ups')}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="active" className="gap-2">
              <Target className="h-4 w-4" />
              Active
              {stats && (
                <Badge variant="secondary" className="ml-1">
                  {stats.open}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="archive" className="gap-2">
              <Archive className="h-4 w-4" />
              Archive
              {archiveData && (
                <Badge variant="secondary" className="ml-1">
                  {archiveData.wonCount + archiveData.lostCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="follow-ups" className="gap-2">
              <CalendarClock className="h-4 w-4" />
              Follow Up
              {followUpsData && followUpsData.totalPending > 0 && (
                <Badge
                  variant="secondary"
                  className={cn(
                    'ml-1',
                    followUpsData.overdueCount > 0 && 'bg-red-100 text-red-700'
                  )}
                >
                  {followUpsData.totalPending}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Active Leads Tab */}
        <TabsContent value="active" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPage(1)
                }}
                className="pl-9"
              />
            </div>

            {/* Status Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  Status
                  {statusFilter.length > 0 && !(statusFilter.length === 1 && statusFilter[0] === 'open') && (
                    <span className="rounded-full bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
                      {statusFilter.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(['open', 'won', 'lost', 'archived'] as LeadStatus[]).map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={statusFilter.includes(status)}
                    onCheckedChange={(checked) => {
                      setStatusFilter(
                        checked
                          ? [...statusFilter, status]
                          : statusFilter.filter((s) => s !== status)
                      )
                      setPage(1)
                    }}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Source Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  Source
                  {sourceFilter.length > 0 && (
                    <span className="rounded-full bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
                      {sourceFilter.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Filter by Source</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(['website', 'phone', 'email', 'referral', 'advertising', 'walk_in', 'other'] as LeadSource[]).map((source) => (
                  <DropdownMenuCheckboxItem
                    key={source}
                    checked={sourceFilter.includes(source)}
                    onCheckedChange={(checked) => {
                      setSourceFilter(
                        checked
                          ? [...sourceFilter, source]
                          : sourceFilter.filter((s) => s !== source)
                      )
                      setPage(1)
                    }}
                  >
                    {source.replace('_', '-').charAt(0).toUpperCase() + source.slice(1).replace('_', ' ')}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Stage Filter */}
            {stages && stages.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    Stage
                    {stageFilter.length > 0 && (
                      <span className="rounded-full bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
                        {stageFilter.length}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Filter by Stage</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {stages.filter(s => !s.isWon && !s.isLost).map((stage) => (
                    <DropdownMenuCheckboxItem
                      key={stage.id}
                      checked={stageFilter.includes(stage.id)}
                      onCheckedChange={(checked) => {
                        setStageFilter(
                          checked
                            ? [...stageFilter, stage.id]
                            : stageFilter.filter((s) => s !== stage.id)
                        )
                        setPage(1)
                      }}
                    >
                      <span
                        className="mr-2 h-2 w-2 rounded-full"
                        style={{ backgroundColor: stage.color }}
                      />
                      {stage.name}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <Button variant="ghost" onClick={clearFilters} className="text-muted-foreground">
                Clear filters
              </Button>
            )}

            {/* View Toggle */}
            <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'list' | 'kanban')} className="ml-auto">
              <TabsList className="grid w-24 grid-cols-2">
                <TabsTrigger value="list" className="px-2">
                  <LayoutList className="h-4 w-4" />
                </TabsTrigger>
                <TabsTrigger value="kanban" className="px-2">
                  <Kanban className="h-4 w-4" />
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Kanban View */}
          {viewMode === 'kanban' && (
            <LeadKanbanBoard filters={filters} />
          )}

          {/* Active Leads Table (List View) */}
          {viewMode === 'list' && (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle>Active Leads</CardTitle>
                <CardDescription>
                  {leadsData?.total
                    ? `${leadsData.total} lead${leadsData.total === 1 ? '' : 's'} found`
                    : 'No leads yet'}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-0 sm:px-6">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <Button
                            variant="ghost"
                            className="h-8 p-0 font-medium hover:bg-transparent"
                            onClick={() => handleSort('created_at')}
                          >
                            Created
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          </Button>
                        </TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            className="h-8 p-0 font-medium hover:bg-transparent"
                            onClick={() => handleSort('customer_name')}
                          >
                            Customer
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          </Button>
                        </TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            className="h-8 p-0 font-medium hover:bg-transparent"
                            onClick={() => handleSort('score')}
                          >
                            Score
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          </Button>
                        </TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            className="h-8 p-0 font-medium hover:bg-transparent"
                            onClick={() => handleSort('estimated_value')}
                          >
                            Value
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          </Button>
                        </TableHead>
                        <TableHead>Next Follow-up</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leadsLoading ? (
                        Array.from({ length: 5 }).map((_, i) => (
                          <TableRow key={i}>
                            {Array.from({ length: 7 }).map((_, j) => (
                              <TableCell key={j}>
                                <Skeleton className="h-4 w-20" />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : leadsData?.leads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center">
                            {filters.search || activeFilterCount > 0
                              ? 'No leads match your search criteria.'
                              : 'No active leads. Leads will appear here when customers start the booking flow or you add them manually.'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        leadsData?.leads.map((lead, index) => (
                          <TableRow
                            key={lead.id}
                            className={cn(
                              'cursor-pointer hover:bg-muted/50 group',
                              keyboardNav.selectedIndex === index && 'bg-primary/10 ring-1 ring-primary/30'
                            )}
                            onClick={() => navigate(`${basePath}/leads/${lead.id}`)}
                          >
                            <TableCell>
                              <LeadAgeIndicator
                                createdAt={lead.createdAt}
                                firstContactedAt={lead.firstContactedAt}
                                size="sm"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{lead.customerName}</p>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    {lead.customerEmail && (
                                      <span className="flex items-center gap-1 truncate">
                                        <Mail className="h-3 w-3 flex-shrink-0" />
                                        <span className="truncate">{lead.customerEmail}</span>
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {/* Quick actions on hover */}
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                  <LeadQuickActionsRow
                                    phone={lead.customerPhone}
                                    email={lead.customerEmail}
                                  />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {lead.customerPhone ? (
                                <a href={`tel:${lead.customerPhone}`} className="text-primary hover:underline text-sm" onClick={(e) => e.stopPropagation()}>
                                  {lead.customerPhone}
                                </a>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {lead.stage && <LeadStageBadge stage={lead.stage} />}
                            </TableCell>
                            <TableCell>
                              <LeadScoreBadge
                                score={lead.score}
                                breakdown={lead.scoreBreakdown}
                                showLabel={false}
                                size="sm"
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              {formatCurrency(lead.estimatedValue)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {lead.nextFollowUpAt ? (
                                  <div className={cn(
                                    'flex items-center gap-1.5',
                                    new Date(lead.nextFollowUpAt) < new Date()
                                      ? 'text-red-600 font-medium'
                                      : 'text-muted-foreground'
                                  )}>
                                    {new Date(lead.nextFollowUpAt) < new Date() && (
                                      <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                                      </span>
                                    )}
                                    {formatDateTime(lead.nextFollowUpAt)}
                                  </div>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setQuickFollowUpLead({ id: lead.id, name: lead.customerName })
                                    }}
                                    title="Schedule follow-up"
                                  >
                                    <CalendarPlus className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {leadsData && leadsData.totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between border-t pt-4">
                    <p className="text-sm text-muted-foreground">
                      Showing {((page - 1) * pageSize) + 1} to{' '}
                      {Math.min(page * pageSize, leadsData.total)} of {leadsData.total} leads
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {page} of {leadsData.totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={page === leadsData.totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Archive Tab */}
        <TabsContent value="archive" className="space-y-4 mt-4">
          {/* Archive Sub-tabs */}
          <div className="flex items-center gap-4">
            <Tabs value={archiveOutcome} onValueChange={(v) => {
              setArchiveOutcome(v as 'all' | 'won' | 'lost')
              setArchivePage(1)
            }}>
              <TabsList>
                <TabsTrigger value="all" className="gap-2">
                  All
                  <Badge variant="secondary">{(archiveData?.wonCount || 0) + (archiveData?.lostCount || 0)}</Badge>
                </TabsTrigger>
                <TabsTrigger value="won" className="gap-2">
                  <Trophy className="h-4 w-4 text-green-600" />
                  Won
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    {archiveData?.wonCount || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="lost" className="gap-2">
                  <ThumbsDown className="h-4 w-4 text-gray-500" />
                  Lost
                  <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                    {archiveData?.lostCount || 0}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Conversion Summary Card */}
          {archiveData && (archiveData.wonCount > 0 || archiveData.lostCount > 0) && (
            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-8">
                  <div>
                    <p className="text-sm text-muted-foreground">Win Rate</p>
                    <p className="text-2xl font-bold text-green-600">
                      {archiveData.wonCount + archiveData.lostCount > 0
                        ? Math.round((archiveData.wonCount / (archiveData.wonCount + archiveData.lostCount)) * 100)
                        : 0}%
                    </p>
                  </div>
                  <div className="h-12 w-px bg-border" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Won</p>
                    <p className="text-2xl font-bold">{archiveData.wonCount}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Lost</p>
                    <p className="text-2xl font-bold">{archiveData.lostCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Archive Table */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle>
                {archiveOutcome === 'all' ? 'All Archived Leads' : archiveOutcome === 'won' ? 'Won Leads' : 'Lost Leads'}
              </CardTitle>
              <CardDescription>
                {archiveData?.total
                  ? `${archiveData.total} lead${archiveData.total === 1 ? '' : 's'} in archive`
                  : 'No archived leads yet'}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0 sm:px-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Outcome</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Closed</TableHead>
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archiveLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 7 }).map((_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-20" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : archiveData?.leads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center">
                          No archived leads yet. Leads marked as won or lost will appear here.
                        </TableCell>
                      </TableRow>
                    ) : (
                      archiveData?.leads.map((lead) => (
                        <TableRow
                          key={lead.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`${basePath}/leads/${lead.id}`)}
                        >
                          <TableCell>
                            <div>
                              <p className="font-medium">{lead.customerName}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {lead.customerEmail && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {lead.customerEmail}
                                  </span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {lead.customerPhone ? (
                              <a href={`tel:${lead.customerPhone}`} className="text-primary hover:underline text-sm" onClick={(e) => e.stopPropagation()}>
                                {lead.customerPhone}
                              </a>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <LeadStatusBadge status={lead.status} />
                          </TableCell>
                          <TableCell className="font-medium">
                            {formatCurrency(lead.estimatedValue)}
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            {lead.status === 'lost' && lead.lostReason && (
                              <p className="text-sm text-muted-foreground truncate" title={lead.lostReason}>
                                {lead.lostReason}
                              </p>
                            )}
                            {lead.outcomeNotes && (
                              <p className="text-sm text-muted-foreground truncate" title={lead.outcomeNotes}>
                                {lead.outcomeNotes}
                              </p>
                            )}
                            {!lead.lostReason && !lead.outcomeNotes && (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDateTime(lead.updatedAt)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleReopenLead(lead.id)
                              }}
                              disabled={updateLead.isPending}
                            >
                              <RotateCcw className="mr-2 h-4 w-4" />
                              Reopen
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Archive Pagination */}
              {archiveData && archiveData.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between border-t pt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {((archivePage - 1) * pageSize) + 1} to{' '}
                    {Math.min(archivePage * pageSize, archiveData.total)} of {archiveData.total} leads
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setArchivePage(archivePage - 1)}
                      disabled={archivePage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {archivePage} of {archiveData.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setArchivePage(archivePage + 1)}
                      disabled={archivePage === archiveData.totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Follow Up Tab */}
        <TabsContent value="follow-ups" className="space-y-4 mt-4">
          {/* Follow-up Sub-filter tabs */}
          <div className="flex items-center gap-4">
            <Tabs value={followUpFilter} onValueChange={(v) => {
              setFollowUpFilter(v as 'all' | 'overdue' | 'today' | 'upcoming')
              setFollowUpPage(1)
            }}>
              <TabsList>
                <TabsTrigger value="all" className="gap-2">
                  All
                  <Badge variant="secondary">{followUpsData?.totalPending || 0}</Badge>
                </TabsTrigger>
                <TabsTrigger value="overdue" className="gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  Overdue
                  <Badge variant="secondary" className={cn(
                    (followUpsData?.overdueCount || 0) > 0 && 'bg-red-100 text-red-700'
                  )}>
                    {followUpsData?.overdueCount || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="today" className="gap-2">
                  Today
                  <Badge variant="secondary" className={cn(
                    (followUpsData?.todayCount || 0) > 0 && 'bg-amber-100 text-amber-700'
                  )}>
                    {followUpsData?.todayCount || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="upcoming" className="gap-2">
                  Upcoming
                  <Badge variant="secondary">{followUpsData?.upcomingCount || 0}</Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Follow-ups Table */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle>Scheduled Follow-Ups</CardTitle>
              <CardDescription>
                {followUpsData?.total
                  ? `${followUpsData.total} follow-up${followUpsData.total === 1 ? '' : 's'} ${followUpFilter === 'all' ? 'pending' : followUpFilter}`
                  : 'No follow-ups scheduled'}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0 sm:px-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Follow-Up</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead className="w-[80px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {followUpsLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 7 }).map((_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-20" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : followUpsData?.followUps.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center">
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <CalendarClock className="h-8 w-8" />
                            <p>No follow-ups scheduled.</p>
                            <p className="text-sm">Schedule follow-ups from lead detail pages.</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      followUpsData?.followUps.map((followUp) => {
                        const dueDate = new Date(followUp.dueAt)
                        const now = new Date()
                        const isOverdue = dueDate < now
                        const isToday = dueDate.toDateString() === now.toDateString()

                        return (
                          <TableRow
                            key={followUp.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`${basePath}/leads/${followUp.leadId}`)}
                          >
                            <TableCell>
                              <div className={cn(
                                'flex items-center gap-2',
                                isOverdue && 'text-red-600 font-medium',
                                isToday && !isOverdue && 'text-amber-600 font-medium'
                              )}>
                                {isOverdue && (
                                  <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                                  </span>
                                )}
                                {formatDateTime(followUp.dueAt)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{followUp.leadName}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  {followUp.leadEmail && (
                                    <span className="flex items-center gap-1 truncate">
                                      <Mail className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">{followUp.leadEmail}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {followUp.leadPhone ? (
                                <a href={`tel:${followUp.leadPhone}`} className="text-primary hover:underline text-sm" onClick={(e) => e.stopPropagation()}>
                                  {followUp.leadPhone}
                                </a>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="max-w-[200px]">
                                <p className="font-medium truncate">{followUp.title}</p>
                                {followUp.description && (
                                  <p className="text-xs text-muted-foreground truncate">{followUp.description}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <LeadStageBadge stage={{ name: followUp.leadStageName, color: followUp.leadStageColor }} />
                            </TableCell>
                            <TableCell>
                              <LeadScoreBadge score={followUp.leadScore} showLabel={false} size="sm" />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  updateFollowUp.mutate({
                                    followUpId: followUp.id,
                                    leadId: followUp.leadId,
                                    status: 'completed',
                                  })
                                }}
                                disabled={updateFollowUp.isPending}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Follow-ups Pagination */}
              {followUpsData && followUpsData.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between border-t pt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {((followUpPage - 1) * pageSize) + 1} to{' '}
                    {Math.min(followUpPage * pageSize, followUpsData.total)} of {followUpsData.total} follow-ups
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFollowUpPage(followUpPage - 1)}
                      disabled={followUpPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {followUpPage} of {followUpsData.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFollowUpPage(followUpPage + 1)}
                      disabled={followUpPage === followUpsData.totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Outcome Dialog */}
      <LeadOutcomeDialog
        type={outcomeDialog.type}
        isOpen={outcomeDialog.isOpen}
        onClose={() => {
          outcomeDialog.close()
          setSelectedLeadForOutcome(null)
        }}
        onSubmit={handleOutcomeSubmit}
        leadName={selectedLeadForOutcome?.name || ''}
        estimatedValue={selectedLeadForOutcome?.value}
        isLoading={updateLead.isPending}
      />

      {/* Quick Follow-Up Dialog */}
      {quickFollowUpLead && (
        <QuickFollowUpDialog
          leadId={quickFollowUpLead.id}
          leadName={quickFollowUpLead.name}
          open={!!quickFollowUpLead}
          onOpenChange={(open) => {
            if (!open) setQuickFollowUpLead(null)
          }}
        />
      )}

      {/* Keyboard Shortcuts Help Dialog */}
      <LeadKeyboardHelp
        isOpen={keyboardNav.showHelp}
        onClose={() => keyboardNav.setShowHelp(false)}
      />
    </div>
  )
}
